import { useState, useMemo, useRef, createRef, RefObject } from 'react'
import {
  Box,
  Typography,
  TextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from '@mui/material'
import { Modal, Button } from 'Common/Supplyvue-ui'
import {
  GridColDef,
  GridRowSelectionModel,
  GridValidRowModel,
} from '@mui/x-data-grid-premium'

// Define the sx props for styled components
const modalBodySx = {
  display: 'flex',
  flexDirection: 'column',
  padding: '20px',
  minWidth: '600px',
  minHeight: '200px',
  maxHeight: '80vh',
  overflowY: 'auto',
}

const footerSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
}

const titleSx = {
  marginBottom: '20px',
}

const selectedRowsInfoSx = {
  marginBottom: '10px',
}

const formFieldContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '16px',
  width: '100%',
}

const formFieldSx = {
  marginBottom: '0px',
}

// 'value', the percentage operations, or the value of one of the column's
// own bulkActions.
type OperationType = string

interface BulkUpdateValue {
  value: string | number | null
  operation: OperationType
}

interface BulkUpdateValues {
  [key: string]: string | number | null | BulkUpdateValue
}

/**
 * A column's own bulk operation, worked out from each selected row (e.g.
 * clear an override, or slow a cycle down one step from where it is now).
 */
export type BulkAction = {
  value: string
  label: string
  apply: (row: GridValidRowModel) => unknown
}

export type ExtendedGridColDef = GridColDef & {
  valueOptions?: string[] | number[]
  bulkActions?: BulkAction[]
}

interface BulkUpdateModalProps {
  open: boolean
  onClose: () => void
  selectedRows: GridRowSelectionModel
  columns: ExtendedGridColDef[]
  onApply: (values: BulkUpdateValues) => void
}

export const BulkUpdateModal = ({
  open,
  onClose,
  selectedRows,
  columns,
  onApply,
}: BulkUpdateModalProps) => {
  // Internal state that won't affect parent rendering
  const [internalValues, setInternalValues] = useState<BulkUpdateValues>({})
  // Track operation type for each column
  const [operations, setOperations] = useState<Record<string, OperationType>>(
    {}
  )
  // Create refs for text fields for keyboard navigation
  const textFieldRefs = useRef<RefObject<HTMLInputElement>[]>([])

  // Get only editable columns
  const editableColumns = useMemo(() => {
    return columns.filter((column) => {
      return (
        column.editable === true &&
        column.field !== 'id' &&
        column.field !== '__check__' &&
        column.field !== 'actions' &&
        column.field
      )
    })
  }, [columns])

  // Initialize refs when columns change
  useMemo(() => {
    textFieldRefs.current = editableColumns.map(() =>
      createRef<HTMLInputElement>()
    )
  }, [editableColumns])

  // Reset values when modal opens and focus first field
  useMemo(() => {
    if (open) {
      setInternalValues({})
      // Initialize all operations to 'value'
      const initialOperations: Record<string, OperationType> = {}
      for (const column of editableColumns) {
        initialOperations[column.field] = 'value'
      }
      setOperations(initialOperations)
      // Focus the first text field after a short delay to ensure the modal is fully rendered
      setTimeout(() => {
        if (
          textFieldRefs.current.length > 0 &&
          textFieldRefs?.current[0]?.current
        ) {
          textFieldRefs.current[0].current.focus()
        }
      }, 100)
    }
  }, [open, editableColumns])

  // Handle input change - contained within this component
  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = event.target
    setInternalValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const actionFor = (field: string) =>
    editableColumns
      .find((col) => col.field === field)
      ?.bulkActions?.find((action) => action.value === operations[field])

  // Handle operation type change for number columns
  const handleOperationChange = (fieldName: string, value: OperationType) => {
    setOperations((prev) => ({
      ...prev,
      [fieldName]: value,
    }))
    // A column action needs no value: choosing one is the update.
    const column = editableColumns.find((col) => col.field === fieldName)
    const isAction = column?.bulkActions?.some((a) => a.value === value)
    setInternalValues((prev) => {
      const next = { ...prev }
      if (isAction) next[fieldName] = null
      else if (next[fieldName] === null) delete next[fieldName]
      return next
    })
  }

  // Handle apply - only communicate with parent when apply is clicked
  const handleApplyClick = () => {
    if (Object.keys(internalValues).length === 0) {
      onClose()
      return
    }

    // Combine values with operations for number columns
    const finalValues: BulkUpdateValues = {}
    Object.keys(internalValues).forEach((field) => {
      const column = editableColumns.find((col) => col.field === field)
      const isNumberColumn = column?.type === 'number'
      const value = internalValues[field]
      const action = actionFor(field)

      if (action) {
        finalValues[field] = { apply: action.apply } as never
      } else if (isNumberColumn && operations[field] !== 'value') {
        // For number columns with percentage operations, send as object
        finalValues[field] = {
          value: value ?? null,
          operation: operations[field],
        } as BulkUpdateValue
      } else {
        // For regular value updates
        finalValues[field] = value ?? null
      }
    })

    onApply(finalValues)
    onClose()
  }

  // Handle key press for navigation and submission
  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault()

      // If there is a next field, focus on it
      if (index < textFieldRefs.current.length - 1) {
        const nextTextField = textFieldRefs?.current[index + 1]?.current
        if (nextTextField) {
          nextTextField.focus()
        }
        return
      }
      // If this is the last field and we have values, submit the form
      if (Object.keys(internalValues).length > 0) {
        handleApplyClick()
      }
    }
  }

  return (
    <Modal
      open={open}
      handleClose={onClose}
      header="Bulk update"
      body={
        <Box sx={modalBodySx}>
          <Typography variant="h5" sx={titleSx}>
            Bulk update selected Rows
          </Typography>
          <Typography variant="body1" sx={selectedRowsInfoSx}>
            You have selected {selectedRows.ids.size} row(s) to update.
          </Typography>

          {editableColumns.length === 0 ? (
            <Typography variant="body2">
              No editable columns available for bulk update.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ marginBottom: '16px' }}>
                Please enter the values you want to apply to all selected rows.
                Leave a field blank if you don&#39;t want to update that field.
              </Typography>

              <Box sx={formFieldContainerSx}>
                {editableColumns.map((column, index) => {
                  const isNumberColumn = column.type === 'number'
                  const hasValueOptions = column.valueOptions !== undefined
                  const showOperationDropdown =
                    (isNumberColumn && !hasValueOptions) ||
                    Boolean(column.bulkActions?.length)
                  const action = actionFor(column.field)

                  return (
                    <Box
                      key={column.field}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '12px',
                        alignItems: 'center',
                      }}
                    >
                      {showOperationDropdown && (
                        <FormControl sx={{ minWidth: '200px' }}>
                          <InputLabel id={`${column.field}-operation-label`}>
                            Operation
                          </InputLabel>
                          <Select
                            labelId={`${column.field}-operation-label`}
                            value={operations[column.field] || 'value'}
                            onChange={(e) =>
                              handleOperationChange(
                                column.field,
                                e.target.value as OperationType
                              )
                            }
                            label="Operation"
                          >
                            <MenuItem value="value">
                              {column.bulkActions ? 'Set to' : 'Value'}
                            </MenuItem>
                            {column.bulkActions
                              ? column.bulkActions.map((a) => (
                                  <MenuItem key={a.value} value={a.value}>
                                    {a.label}
                                  </MenuItem>
                                ))
                              : [
                                  <MenuItem
                                    key="increase"
                                    value="increase_by_percentage"
                                  >
                                    Increase by %
                                  </MenuItem>,
                                  <MenuItem
                                    key="decrease"
                                    value="decrease_by_percentage"
                                  >
                                    Decrease by %
                                  </MenuItem>,
                                ]}
                          </Select>
                        </FormControl>
                      )}
                      <FormControl sx={{ flex: 1, ...formFieldSx }}>
                        <TextField
                          label={column.headerName}
                          name={column.field}
                          variant="outlined"
                          value={internalValues[column.field] ?? ''}
                          disabled={Boolean(action)}
                          helperText={action ? action.label : undefined}
                          onChange={handleInputChange}
                          onKeyDown={(e) => handleKeyPress(e, index)}
                          inputRef={textFieldRefs.current[index]}
                          select={column.valueOptions !== undefined}
                        >
                          {Array.isArray(column.valueOptions) &&
                            (column.valueOptions as Array<string | number>).map(
                              (option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              )
                            )}
                        </TextField>
                      </FormControl>
                    </Box>
                  )
                })}
              </Box>
            </>
          )}
        </Box>
      }
      footer={
        <Box sx={footerSx}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyClick}
            disabled={
              editableColumns.length === 0 ||
              Object.keys(internalValues).length === 0
            }
          >
            Apply
          </Button>
        </Box>
      }
    />
  )
}
